import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import './MhtCetForm.css';
import MccForm from './MccForm';

const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbx8oEnZ5-b7NOZsCDeZOj0YM16EUWjQMD8CS-X09ip2Uqr-sNR_FlBRNwSkuWzLd6aA/exec";

// ─── OPTION LISTS ───────────────────────────────────────────────
const OFFICES = ['Latur Office', 'Pune Office', 'Kolhapur Office', 'Nanded Office', 'Solapur Office'];
const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Parsi', 'Other'];
const GENDERS = ['Male', 'Female', 'Other'];
const YES_NO = ['Yes', 'No'];
const CERT_STATUS = ['Yes', 'Applied but not Received', 'No'];
const REGIONS = ['Urban', 'Rural'];
const INCOME_RANGES = [
  '₹ 0 - ₹ 50,000 (Up to 50k)',
  '₹ 50,000 - ₹ 1,00,000 (50k - 1 Lakh)',
  '₹ 1,00,000 - ₹ 2,00,000 (1 Lakh - 2 Lakhs)',
  '₹ 2,00,000 - ₹ 3,00,000 (2 Lakhs - 3 Lakhs)',
  '₹ 3,00,000 - ₹ 4,00,000 (3 Lakhs - 4 Lakhs)',
  '₹ 4,00,000 - ₹ 5,00,000 (4 Lakhs - 5 Lakhs)',
  '₹ 5,00,000 - ₹ 6,00,000 (5 Lakhs - 6 Lakhs)',
  '₹ 6,00,000 - ₹ 8,00,000 (6 Lakhs - 8 Lakhs)',
  '₹ 8,00,000 - ₹ 10,00,000 (8 Lakhs - 10 Lakhs)',
  'Above ₹ 10,00,000 (Above 10 Lakhs)'
];
const CATEGORIES = ['Open (General)', 'EWS', 'SC', 'ST', 'VJ A', 'NT B (NT 1)', 'NT C (NT 2)', 'NT D (NT 3)', 'OBC', 'SBC', 'SEBC (Maratha)'];
const BOARDS = ['Maharashtra State Board', 'CBSE', 'ICSE', 'Other'];
const MOTHER_TONGUES = ['Marathi', 'Hindi', 'Urdu', 'Kannada', 'Gujarati', 'Telugu', 'Tamil', 'Malayalam', 'Bengali', 'Punjabi', 'Sindhi', 'Other'];
const SSC_LANGUAGES = ['None', 'Urdu', 'Parsi', 'Arabic'];
const PASSING_YEARS = Array.from({ length: 27 }, (_, i) => (2026 - i).toString());
const SPECIAL_RESERVATIONS = ['Hilly', 'Defence 1', 'Defence 2', 'Defence 3', 'MKB'];

const CASTE_CATEGORIES = ['SC', 'ST', 'VJ A', 'NT B (NT 1)', 'NT C (NT 2)', 'NT D (NT 3)', 'OBC', 'SBC', 'SEBC (Maratha)'];
const NCL_CATEGORIES = ['VJ A', 'NT B (NT 1)', 'NT C (NT 2)', 'NT D (NT 3)', 'OBC', 'SBC', 'SEBC (Maratha)'];

const CASTE_AUTHORITIES = [
  'Sub-Divisional Officer (SDO)',
  'Tahsildar',
  'Deputy Collector',
  'Executive Magistrate',
  'District Magistrate / Collector',
  'Other'
];

const VALIDITY_AUTHORITIES = [
  'District Caste Certificate Scrutiny Committee',
  'Divisional Caste Scrutiny Committee',
  'Other'
];

const MAHARASHTRA_DISTRICTS = [
  'Ahmednagar', 'Akola', 'Amravati', 'Beed', 'Bhandara', 'Buldhana', 'Chandrapur',
  'Chhatrapati Sambhajinagar', 'Dharashiv', 'Dhule', 'Gadchiroli', 'Gondia', 'Hingoli', 'Jalgaon',
  'Jalna', 'Kolhapur', 'Latur', 'Mumbai City', 'Mumbai Suburban', 'Nagpur', 'Nanded',
  'Nandurbar', 'Nashik', 'Palghar', 'Parbhani', 'Pune', 'Raigad',
  'Ratnagiri', 'Sangli', 'Satara', 'Sindhudurg', 'Solapur', 'Thane', 'Wardha',
  'Washim', 'Yavatmal'
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry',
  'Chandigarh', 'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli', 'Daman and Diu', 'Lakshadweep'
];

// ─── SECTION STEP DEFINITIONS ─────────────────────────────────
const SECTION_STEPS = [
  { id: 'sec1', title: 'Section 1 — Registration Info', icon: 'fa-id-card' },
  { id: 'sec2', title: 'Section 2 — NEET UG 2026 Details', icon: 'fa-flask' },
  { id: 'sec3', title: 'Section 3 — Defence Status', icon: 'fa-shield-alt' },
  { id: 'sec4', title: 'Section 4 — Personal Details', icon: 'fa-user' },
  { id: 'sec4b', title: 'Section 4B — Contact & Identity', icon: 'fa-phone-alt' },
  { id: 'sec5', title: 'Section 5 — Address & Domicile', icon: 'fa-map-marker-alt' },
  { id: 'sec6', title: 'Section 6 — Reservation & Income', icon: 'fa-rupee-sign' },
  { id: 'sec8', title: 'Section 8 — Category Certificates', icon: 'fa-file-certificate' },
  { id: 'sec8a', title: 'Section 8A — Non-Creamy Layer', icon: 'fa-certificate' },
  { id: 'sec9', title: 'Section 9 — PWD & Minority', icon: 'fa-wheelchair' },
  { id: 'sec10', title: 'Section 10 — SSC / 10th Details', icon: 'fa-school' },
  { id: 'sec11', title: 'Section 11 — HSC / 12th Details', icon: 'fa-graduation-cap' },
  { id: 'sec12', label: 'Sec 12', title: 'Section 12 — HSC / 12th Marks', icon: 'fa-chart-bar' },
  { id: 'sec13', label: 'Sec 13', title: 'Section 13 — Special Reservation', icon: 'fa-star' },
  { id: 'sec14', label: 'Sec 14', title: 'Section 14 — Final Review & Summary', icon: 'fa-clipboard-check' }
];

// ─── INITIAL FORM STATE ─────────────────────────────────────────
const initialFormData = {
  regReceiptNumber: '', registeredWith: '',
  neetRollNumber: '', neetApplicationNumber: '', neetScore: '', neetAIRank: '',
  parentDeputation: '',
  nameChanged: '', studentName: '', fathersName: '', surname: '', mothersName: '', gender: '', dateOfBirth: '',
  mobileNumber: '', emailId: '', alternateMobile: '', aadharNumber: '', apaarId: '', religion: '',
  address: '', city: '', taluka: '', district: '', state: 'Maharashtra', pinCode: '', regionOfResidence: '', domicileMaharashtra: '',
  familyAnnualIncome: '', reservationCategory: '',
  hasEwsCertificate: '', ewsCertificateNumber: '',
  ewsCertificateDate: '', ewsCertificateAuthority: '', ewsCertificateAuthorityCustom: '',
  hasCasteCertificate: '', casteCertificateNumber: '', hasCasteValidityCertificate: '', casteValidityCertificateNumber: '',
  casteCertificateDate: '', casteCertificateAuthority: '', casteCertificateAuthorityCustom: '',
  casteValidityCertificateDate: '', casteValidityCertificateAuthority: '', casteValidityCertificateAuthorityCustom: '',
  hasNclCertificate: '', nclCertificateNumber: '',
  nclCertificateDate: '', nclCertificateAuthority: '', nclCertificateAuthorityCustom: '',
  pwd: '', linguisticMinority: '', motherTongue: '',
  sscPassingYear: '', sscLanguageTaken: '', sscState: 'Maharashtra', sscDistrict: '', sscTaluka: '', sscBoard: '',
  hscPassingYear: '', hscSeatNumber: '', hscState: 'Maharashtra', hscDistrict: '', hscTaluka: '', hscBoard: '',
  hscPhysicsMarks: '', hscChemistryMarks: '', hscBiologyMarks: '', hscEnglishMarks: '',
  specialReservation: [],
  hillyVillageName: '', hillyTalukaName: '',
};

// ─── COMPONENT ──────────────────────────────────────────────────
export default function MhtCetForm() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const pdfRef = React.useRef(null);

  const getFullName = () => {
    let name = formData.studentName || '';
    let surname = formData.surname || '';
    if (name.toUpperCase().includes(surname.toUpperCase()) && surname.length > 0) {
      return name;
    }
    return `${name} ${surname}`.trim();
  };
  const [selectedForm, setSelectedForm] = useState(null);
  const [settings, setSettings] = useState({
    form_state_active: true,
    form_mcc_active: true
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const res = await axios.get(`${backendUrl}/api/public/settings`);
        setSettings({
          form_state_active: res.data?.form_state_active !== false,
          form_mcc_active: res.data?.form_mcc_active !== false
        });
      } catch (err) {
        console.error("Failed to load settings", err);
      }
    };
    fetchSettings();
  }, []);

  // PDF layout helper functions
  const sh = (num, title) => (
    <tr>
      <td colSpan={2} style={{ background: '#0ea5e9', color: '#fff', padding: '7px 12px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        {num}. {title}
      </td>
    </tr>
  );

  const dr = (label, val, shade) => (
    <tr style={{ background: shade ? '#f9fafb' : '#ffffff' }}>
      <td style={{ padding: '6px 12px', color: '#555', fontSize: '10.5px', width: '42%', borderBottom: '1px solid #f0f0f0' }}>{label}</td>
      <td style={{ padding: '6px 12px', fontSize: '10.5px', fontWeight: '600', color: '#111', borderBottom: '1px solid #f0f0f0' }}>{val || '—'}</td>
    </tr>
  );

  // ─── CATEGORY HELPERS ──────────────────────────────────
  const isEwsEligible = () => formData.reservationCategory === 'EWS';
  const isOpenGeneral = () => formData.reservationCategory === 'Open (General)';
  const isCasteCategory = () => CASTE_CATEGORIES.includes(formData.reservationCategory);
  const isNclCategory = () => NCL_CATEGORIES.includes(formData.reservationCategory);
  const isHillySelected = () => (formData.specialReservation || []).includes('Hilly');

  // Compute active valid section steps:
  //  - skip sec8 & sec8a entirely if Open (General) selected
  //  - skip sec8a if not NCL category
  const activeSteps = SECTION_STEPS.filter(s => {
    if (s.id === 'sec8' && isOpenGeneral()) return false;
    if (s.id === 'sec8a' && (isOpenGeneral() || !isNclCategory())) return false;
    return true;
  });

  const currentStep = activeSteps[stepIndex] || activeSteps[0];

  // ─── HANDLERS ───────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    const uppercaseFields = ['studentName', 'fathersName', 'surname', 'mothersName', 'address', 'city', 'taluka', 'sscTaluka', 'hscTaluka', 'hillyVillageName', 'hillyTalukaName'];
    if (uppercaseFields.includes(name)) newValue = value.toUpperCase();

    const numericFields = ['aadharNumber', 'mobileNumber', 'alternateMobile', 'pinCode', 'neetScore', 'neetAIRank', 'neetRollNumber', 'neetApplicationNumber', 'hscPhysicsMarks', 'hscChemistryMarks', 'hscBiologyMarks', 'hscEnglishMarks', 'regReceiptNumber', 'sscPassingYear', 'hscPassingYear', 'apaarId'];
    if (numericFields.includes(name)) newValue = value.replace(/\D/g, '');
    // hscSeatNumber is alphanumeric — strip only disallowed characters, allow letters + digits
    if (name === 'hscSeatNumber') newValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (name === 'aadharNumber') newValue = newValue.slice(0, 12);
    if (name === 'apaarId') newValue = newValue.slice(0, 12);
    if (name === 'neetApplicationNumber') newValue = newValue.slice(0, 12);
    if (name === 'neetRollNumber') newValue = newValue.slice(0, 10);
    if (name === 'mobileNumber' || name === 'alternateMobile') newValue = newValue.slice(0, 10);
    if (name === 'pinCode') newValue = newValue.slice(0, 6);
    if (name === 'regReceiptNumber') newValue = newValue.slice(0, 4);

    if (name === 'neetScore') {
      newValue = newValue.slice(0, 3);
      if (newValue && parseInt(newValue, 10) > 720) newValue = '720';
    }
    if (name === 'neetAIRank') {
      newValue = newValue.slice(0, 7);
      if (newValue && parseInt(newValue, 10) > 2100000) newValue = '2100000';
    }
    if (['hscPhysicsMarks', 'hscChemistryMarks', 'hscBiologyMarks', 'hscEnglishMarks'].includes(name)) {
      newValue = newValue.slice(0, 3);
      if (newValue && parseInt(newValue, 10) > 100) newValue = '100';
    }

    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSpecialReservation = (val) => {
    setFormData(prev => {
      const current = prev.specialReservation || [];
      if (val === 'None') return { ...prev, specialReservation: ['None'], hillyVillageName: '', hillyTalukaName: '' };
      const without = current.filter(v => v !== 'None');
      const updated = without.includes(val) ? without.filter(v => v !== val) : [...without, val];
      const cleaned = { ...prev, specialReservation: updated };
      if (!updated.includes('Hilly')) { cleaned.hillyVillageName = ''; cleaned.hillyTalukaName = ''; }
      return cleaned;
    });
    if (errors.specialReservation) setErrors(prev => ({ ...prev, specialReservation: '' }));
  };

  const goToSection = (stepId) => {
    const idx = activeSteps.findIndex(s => s.id === stepId);
    if (idx !== -1) {
      setStepIndex(idx);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ─── COMPULSORY VALIDATION FOR EVERY SECTION STEP ───────
  const validateCurrentStep = () => {
    const e = {};
    const stepId = currentStep.id;

    if (stepId === 'sec1') {
      if (!formData.regReceiptNumber || formData.regReceiptNumber.length !== 4) {
        e.regReceiptNumber = 'Valid 4-digit Reg./Receipt Number compulsory (e.g. 0001)';
      }
      if (!formData.registeredWith) e.registeredWith = 'Registered Office is compulsory';
    }
    if (stepId === 'sec2') {
      if (!formData.neetRollNumber || formData.neetRollNumber.length !== 10) {
        e.neetRollNumber = 'Valid 10-digit NEET Roll Number compulsory (e.g. 3109201656)';
      }
      if (!formData.neetApplicationNumber || formData.neetApplicationNumber.length !== 12) {
        e.neetApplicationNumber = 'Valid 12-digit NEET Application Number compulsory (e.g. 260410187733)';
      }
      const score = parseInt(formData.neetScore, 10);
      if (!formData.neetScore.trim() || isNaN(score) || score <= 0 || score > 720) {
        e.neetScore = 'NEET Score must be between 1 and 720 (Non-zero)';
      }
      const rank = parseInt(formData.neetAIRank, 10);
      if (!formData.neetAIRank.trim() || isNaN(rank) || rank <= 0 || rank > 2100000) {
        e.neetAIRank = 'NEET AIR Rank must be between 1 and 21,00,000';
      }
    }
    if (stepId === 'sec3') {
      if (!formData.parentDeputation) e.parentDeputation = 'Parent Deputation status is compulsory';
    }
    if (stepId === 'sec4') {
      if (!formData.nameChanged) e.nameChanged = 'Name change status is compulsory';
      if (!formData.studentName.trim()) e.studentName = 'Student Name is compulsory';
      if (!formData.fathersName.trim()) e.fathersName = "Father's Name is compulsory";
      if (!formData.surname.trim()) e.surname = 'Surname is compulsory';
      if (!formData.mothersName.trim()) e.mothersName = "Mother's Name is compulsory";
      if (!formData.gender) e.gender = 'Gender is compulsory';
      if (!formData.dateOfBirth) e.dateOfBirth = 'Date of Birth is compulsory';
    }
    if (stepId === 'sec4b') {
      if (!formData.mobileNumber || formData.mobileNumber.length !== 10) e.mobileNumber = 'Valid 10-digit mobile number compulsory';
      if (!formData.emailId || !formData.emailId.includes('@')) e.emailId = 'Valid email ID compulsory';
      if (!formData.alternateMobile || formData.alternateMobile.length !== 10) e.alternateMobile = 'Valid 10-digit alternate mobile compulsory';
      if (!formData.aadharNumber || formData.aadharNumber.length !== 12) e.aadharNumber = 'Valid 12-digit Aadhaar number compulsory';
      if (!formData.apaarId.trim()) {
        e.apaarId = 'APAAR ID is compulsory';
      } else if (formData.apaarId.trim().length !== 12) {
        e.apaarId = 'APAAR ID must be exactly 12 digits';
      }
      if (!formData.religion) e.religion = 'Religion is compulsory';
    }
    if (stepId === 'sec5') {
      if (!formData.address.trim()) e.address = 'Full address is compulsory';
      if (!formData.city.trim()) e.city = 'City is compulsory';
      if (!formData.taluka.trim()) e.taluka = 'Taluka is compulsory';
      if (!formData.district) e.district = 'District is compulsory';
      if (!formData.state) e.state = 'State is compulsory';
      if (!formData.pinCode || formData.pinCode.length !== 6) e.pinCode = 'Valid 6-digit pincode compulsory';
      if (!formData.regionOfResidence) e.regionOfResidence = 'Region of Residence is compulsory';
      if (!formData.domicileMaharashtra) e.domicileMaharashtra = 'Domicile status is compulsory';
    }
    if (stepId === 'sec6') {
      if (!formData.familyAnnualIncome) e.familyAnnualIncome = 'Annual Income is compulsory';
      if (!formData.reservationCategory) e.reservationCategory = 'Category selection is compulsory';
    }
    if (stepId === 'sec8') {
      if (isEwsEligible()) {
        if (!formData.hasEwsCertificate) e.hasEwsCertificate = 'EWS Certificate status is compulsory';
        if (formData.hasEwsCertificate && formData.hasEwsCertificate !== 'No') {
          if (!formData.ewsCertificateNumber.trim()) {
            e.ewsCertificateNumber = 'EWS Certificate/Receipt Number is compulsory';
          }
          if (!formData.ewsCertificateDate) {
            e.ewsCertificateDate = formData.hasEwsCertificate === 'Yes' ? 'EWS Certificate Issuing Date is compulsory' : 'EWS Certificate Application Date is compulsory';
          }
          if (formData.hasEwsCertificate === 'Yes') {
            if (!formData.ewsCertificateAuthority) {
              e.ewsCertificateAuthority = 'EWS Certificate Issuing Authority is compulsory';
            } else if (formData.ewsCertificateAuthority === 'Other' && !formData.ewsCertificateAuthorityCustom.trim()) {
              e.ewsCertificateAuthorityCustom = 'Please specify the custom EWS issuing authority';
            }
          }
        }
      }
      if (isCasteCategory()) {
        if (!formData.hasCasteCertificate) e.hasCasteCertificate = 'Caste Certificate status is compulsory';
        if (formData.hasCasteCertificate && formData.hasCasteCertificate !== 'No') {
          if (!formData.casteCertificateNumber.trim()) {
            e.casteCertificateNumber = 'Caste Certificate Number is compulsory';
          }
          if (!formData.casteCertificateDate) {
            e.casteCertificateDate = formData.hasCasteCertificate === 'Yes' ? 'Caste Certificate Issuing Date is compulsory' : 'Caste Certificate Application Date is compulsory';
          }
          if (formData.hasCasteCertificate === 'Yes') {
            if (!formData.casteCertificateAuthority) {
              e.casteCertificateAuthority = 'Caste Certificate Issuing Authority is compulsory';
            } else if (formData.casteCertificateAuthority === 'Other' && !formData.casteCertificateAuthorityCustom.trim()) {
              e.casteCertificateAuthorityCustom = 'Please specify the custom Caste issuing authority';
            }
          }
        }
        
        if (!formData.hasCasteValidityCertificate) e.hasCasteValidityCertificate = 'Caste Validity Certificate status is compulsory';
        if (formData.hasCasteValidityCertificate && formData.hasCasteValidityCertificate !== 'No') {
          if (!formData.casteValidityCertificateNumber.trim()) {
            e.casteValidityCertificateNumber = 'Caste Validity Number is compulsory';
          }
          if (!formData.casteValidityCertificateDate) {
            e.casteValidityCertificateDate = formData.hasCasteValidityCertificate === 'Yes' ? 'Caste Validity Certificate Issuing Date is compulsory' : 'Caste Validity Certificate Application Date is compulsory';
          }
          if (formData.hasCasteValidityCertificate === 'Yes') {
            if (!formData.casteValidityCertificateAuthority) {
              e.casteValidityCertificateAuthority = 'Caste Validity Certificate Issuing Authority is compulsory';
            } else if (formData.casteValidityCertificateAuthority === 'Other' && !formData.casteValidityCertificateAuthorityCustom.trim()) {
              e.casteValidityCertificateAuthorityCustom = 'Please specify the custom Caste Validity issuing authority';
            }
          }
        }
      }
    }
    if (stepId === 'sec8a') {
      if (isNclCategory()) {
        if (!formData.hasNclCertificate) e.hasNclCertificate = 'Non-Creamy Layer Certificate status is compulsory';
        if (formData.hasNclCertificate && formData.hasNclCertificate !== 'No') {
          if (!formData.nclCertificateNumber.trim()) {
            e.nclCertificateNumber = 'NCL Certificate/Receipt Number is compulsory';
          }
          if (!formData.nclCertificateDate) {
            e.nclCertificateDate = formData.hasNclCertificate === 'Yes' ? 'NCL Certificate Issuing Date is compulsory' : 'NCL Certificate Application Date is compulsory';
          }
          if (formData.hasNclCertificate === 'Yes') {
            if (!formData.nclCertificateAuthority) {
              e.nclCertificateAuthority = 'NCL Certificate Issuing Authority is compulsory';
            } else if (formData.nclCertificateAuthority === 'Other' && !formData.nclCertificateAuthorityCustom.trim()) {
              e.nclCertificateAuthorityCustom = 'Please specify the custom NCL issuing authority';
            }
          }
        }
      }
    }
    if (stepId === 'sec9') {
      if (!formData.pwd) e.pwd = 'PWD selection is compulsory';
      if (!formData.linguisticMinority) e.linguisticMinority = 'Linguistic Minority status is compulsory';
      if (!formData.motherTongue) e.motherTongue = 'Mother Tongue is compulsory';
    }
    if (stepId === 'sec10') {
      const yr = parseInt(formData.sscPassingYear, 10);
      if (!formData.sscPassingYear || formData.sscPassingYear.length !== 4 || isNaN(yr) || yr < 1990 || yr > 2026) {
        e.sscPassingYear = 'Valid 4-digit SSC passing year compulsory (e.g. 2024)';
      }
      if (!formData.sscLanguageTaken) e.sscLanguageTaken = 'SSC Language selection is compulsory';
      if (!formData.sscState) e.sscState = 'SSC State is compulsory';
      if (!formData.sscDistrict) e.sscDistrict = 'SSC District is compulsory';
      if (!formData.sscTaluka.trim()) e.sscTaluka = 'SSC Taluka is compulsory';
      if (!formData.sscBoard) e.sscBoard = 'SSC Board is compulsory';
    }
    if (stepId === 'sec11') {
      const yr = parseInt(formData.hscPassingYear, 10);
      if (!formData.hscPassingYear || formData.hscPassingYear.length !== 4 || isNaN(yr) || yr < 1990 || yr > 2026) {
        e.hscPassingYear = 'Valid 4-digit HSC passing year compulsory (e.g. 2026)';
      }
      if (!formData.hscSeatNumber.trim()) e.hscSeatNumber = 'HSC Seat/Roll Number is compulsory';
      if (!formData.hscState) e.hscState = 'HSC State is compulsory';
      if (!formData.hscDistrict) e.hscDistrict = 'HSC District is compulsory';
      if (!formData.hscTaluka.trim()) e.hscTaluka = 'HSC Taluka is compulsory';
      if (!formData.hscBoard) e.hscBoard = 'HSC Board is compulsory';
    }
    if (stepId === 'sec12') {
      if (!formData.hscPhysicsMarks.trim()) e.hscPhysicsMarks = 'Physics Marks are compulsory';
      if (!formData.hscChemistryMarks.trim()) e.hscChemistryMarks = 'Chemistry Marks are compulsory';
      if (!formData.hscBiologyMarks.trim()) e.hscBiologyMarks = 'Biology Marks are compulsory';
      if (!formData.hscEnglishMarks.trim()) e.hscEnglishMarks = 'English Marks are compulsory';
    }
    if (stepId === 'sec13') {
      if (!formData.specialReservation || formData.specialReservation.length === 0) {
        e.specialReservation = 'Please select at least one Special Reservation option or "None"';
      }
      if (isHillySelected()) {
        if (!formData.hillyVillageName.trim()) e.hillyVillageName = 'Hilly Village Name is compulsory';
        if (!formData.hillyTalukaName.trim()) e.hillyTalukaName = 'Hilly Taluka Name is compulsory';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (stepIndex < activeSteps.length - 1) {
        setStepIndex(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      toast.error('Please fill all compulsory fields marked with *');
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ─── SUBMISSION ────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      toast.error('Please fill all compulsory fields marked with *');
      return;
    }
    setIsSubmitting(true);
    try {
      await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── RENDER CONTROL HELPERS ─────────────────────────────
  const renderInput = (label, name, opts = {}) => {
    const { type = 'text', placeholder = '', disabled = false, min = '', isOptional = false } = opts;
    return (
      <div className="cetform-group">
        <label className="cetform-label">{label}{!isOptional && <span className="cetform-req">*</span>}</label>
        <input
          className={`cetform-input ${errors[name] ? 'error' : ''}`}
          type={type}
          name={name}
          value={formData[name] || ''}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
        />
        {errors[name] && <span className="cetform-error">{errors[name]}</span>}
      </div>
    );
  };

  const renderSelect = (label, name, options, opts = {}) => {
    const { placeholder = '-- Select --', isOptional = false } = opts;
    return (
      <div className="cetform-group">
        <label className="cetform-label">{label}{!isOptional && <span className="cetform-req">*</span>}</label>
        <select
          className={`cetform-select ${errors[name] ? 'error' : ''}`}
          name={name}
          value={formData[name] || ''}
          onChange={handleChange}
        >
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {errors[name] && <span className="cetform-error">{errors[name]}</span>}
      </div>
    );
  };

  const renderRadioGroup = (label, name, options, opts = {}) => {
    const { marathiLabel } = opts;
    return (
      <div className="cetform-group full-width">
        <label className="cetform-label">
          <span>{label}</span>
          <span className="cetform-req">*</span>
        </label>
        {marathiLabel && (
          <div className="cetform-marathi-label">
            <i className="fas fa-language" style={{ marginRight: 6, color: 'var(--cet-accent-blue)' }}></i>
            {marathiLabel}
          </div>
        )}
        <div className="cetform-radio-group">
          {options.map(o => {
            const isYes = o === 'Yes';
            const isNo = o === 'No';
            const isActive = formData[name] === o;
            return (
              <label key={o} className={`cetform-radio-card ${isActive ? (isYes ? 'active-yes' : isNo ? 'active-no' : 'active') : ''}`}>
                <input type="radio" name={name} value={o} checked={isActive} onChange={handleChange} />
                {isYes && <i className="fas fa-check-circle cetform-radio-icon"></i>}
                {isNo && <i className="fas fa-times-circle cetform-radio-icon"></i>}
                <span>{o} {isYes ? '(होय)' : isNo ? '(नाही)' : ''}</span>
              </label>
            );
          })}
        </div>
        {errors[name] && <span className="cetform-error">{errors[name]}</span>}
      </div>
    );
  };

  // ─── INDIVIDUAL SECTION PAGE RENDERERS ─────────────────

  const renderSectionContent = () => {
    switch (currentStep.id) {
      case 'sec1':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderInput('Admissions Made Easy Reg./Receipt Number (4 digits)', 'regReceiptNumber', { placeholder: 'e.g. 0001' })}
              {renderSelect('Registered With', 'registeredWith', OFFICES)}
            </div>
          </div>
        );

      case 'sec2':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderInput('NEET UG 2026 Roll Number (10 digits)', 'neetRollNumber', { placeholder: 'e.g. 3109201656' })}
              {renderInput('NEET UG 2026 Application Number (12 digits)', 'neetApplicationNumber', { placeholder: 'e.g. 260410187733' })}
              {renderInput('NEET 2026 Score (1 - 720)', 'neetScore', { placeholder: 'e.g. 620' })}
              {renderInput('NEET 2026 All India Rank (1 - 21 Lakh)', 'neetAIRank', { placeholder: 'e.g. 15420' })}
            </div>
          </div>
        );

      case 'sec3':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderRadioGroup(
                'Is parent transferred/posted/deputed in Maharashtra & employee of Govt of India (Defence, BSF, CRPF, Central Govt)?',
                'parentDeputation',
                YES_NO,
                { marathiLabel: 'पालक महाराष्ट्रात बदली / पदस्थापना / डेप्युटेशनवर आले आहेत का आणि ते भारत सरकारचे (संरक्षण, BSF, CRPF, केंद्र सरकार) कर्मचारी आहेत का?' }
              )}
            </div>
          </div>
        );

      case 'sec4':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderRadioGroup(
                'Whether your NAME is changed or updated after 10th or after Marriage',
                'nameChanged',
                YES_NO,
                { marathiLabel: 'दहावीनंतर किंवा लग्नानंतर तुमचे नाव बदलले आहे किंवा अपडेट झाले आहे का?' }
              )}
              {renderInput('Student Name (As per NEET Score Card)', 'studentName', { placeholder: 'FULL NAME' })}
              {renderInput("Father's Name (Only Name)", 'fathersName', { placeholder: 'FATHER NAME' })}
              {renderInput('Surname', 'surname', { placeholder: 'SURNAME' })}
              {renderInput("Mother's Name (Only Name)", 'mothersName', { placeholder: 'MOTHER NAME' })}
              {renderSelect('Gender', 'gender', GENDERS)}
              {renderInput('Date of Birth', 'dateOfBirth', { type: 'date' })}
            </div>
          </div>
        );

      case 'sec4b':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderInput('Mobile Number (To be Registered for Process)', 'mobileNumber', { placeholder: '10-digit mobile' })}
              {renderInput('Email ID (To be Registered for Process)', 'emailId', { type: 'email', placeholder: 'email@example.com' })}
              {renderInput('Alternate Mobile Number', 'alternateMobile', { placeholder: '10-digit mobile' })}
              {renderInput('AADHAR Card Number', 'aadharNumber', { placeholder: '12-digit Aadhaar' })}
              {renderInput('APAAR ID', 'apaarId', { placeholder: 'APAAR ID' })}
              {renderSelect('Religion', 'religion', RELIGIONS)}
            </div>
          </div>
        );

      case 'sec5':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              <div className="cetform-group full-width">
                <label className="cetform-label">Address<span className="cetform-req">*</span></label>
                <textarea
                  className={`cetform-textarea ${errors.address ? 'error' : ''}`}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Full residential address"
                  rows={3}
                />
                {errors.address && <span className="cetform-error">{errors.address}</span>}
              </div>
              {renderInput('City', 'city', { placeholder: 'City' })}
              {renderInput('Taluka', 'taluka', { placeholder: 'Taluka' })}
              {renderInput('District', 'district', { placeholder: 'Enter your district' })}
              {renderSelect('State', 'state', INDIAN_STATES)}
              {renderInput('Pin Code', 'pinCode', { placeholder: '6-digit pincode' })}
              {renderSelect('Region of Residence', 'regionOfResidence', REGIONS)}
              {renderRadioGroup(
                'Domicile of Maharashtra',
                'domicileMaharashtra',
                YES_NO,
                { marathiLabel: 'महाराष्ट्र राज्याचे अधिवास (Domicile) प्रमाणपत्र आहे का?' }
              )}
            </div>
          </div>
        );

      case 'sec6':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderSelect('Family Annual Income', 'familyAnnualIncome', INCOME_RANGES)}
              {renderSelect('Reservation Category', 'reservationCategory', CATEGORIES)}
            </div>
          </div>
        );

      case 'sec8':
        return (
          <div className="cetform-section">
            {isEwsEligible() && (
              <div className="cetform-grid">
                {renderSelect('Having State EWS Certificate (Only for General Candidates)', 'hasEwsCertificate', CERT_STATUS)}
                {formData.hasEwsCertificate && formData.hasEwsCertificate !== 'No' && (
                  <>
                    {renderInput('EWS Certificate Number (Receipt Number if applied)', 'ewsCertificateNumber', { placeholder: 'Certificate / Receipt Number' })}
                    {renderInput(
                      formData.hasEwsCertificate === 'Yes' ? 'EWS Certificate Issuing Date' : 'EWS Certificate Application Date',
                      'ewsCertificateDate',
                      { type: 'date' }
                    )}
                    {renderSelect(
                      formData.hasEwsCertificate === 'Yes' ? 'EWS Certificate Issuing Authority' : 'EWS Certificate Issuing Authority (Optional)',
                      'ewsCertificateAuthority',
                      CASTE_AUTHORITIES,
                      { isOptional: formData.hasEwsCertificate === 'Applied but not Received' }
                    )}
                    {formData.ewsCertificateAuthority === 'Other' && renderInput(
                      formData.hasEwsCertificate === 'Yes' ? 'Please Specify EWS Issuing Authority' : 'Please Specify EWS Issuing Authority (Optional)',
                      'ewsCertificateAuthorityCustom',
                      { placeholder: 'Specify authority details', isOptional: formData.hasEwsCertificate === 'Applied but not Received' }
                    )}
                  </>
                )}
              </div>
            )}

            {isCasteCategory() && (
              <div className="cetform-grid">
                {renderSelect('Having Caste Certificate (SC/ST/VJ/NT/OBC/SBC/SEBC)', 'hasCasteCertificate', CERT_STATUS)}
                {formData.hasCasteCertificate && formData.hasCasteCertificate !== 'No' && (
                  <>
                    {renderInput('Caste Certificate Number (MRC No / Outward No / Sr No)', 'casteCertificateNumber', { placeholder: 'Certificate Number' })}
                    {renderInput(
                      formData.hasCasteCertificate === 'Yes' ? 'Caste Certificate Issuing Date' : 'Caste Certificate Application Date',
                      'casteCertificateDate',
                      { type: 'date' }
                    )}
                    {renderSelect(
                      formData.hasCasteCertificate === 'Yes' ? 'Caste Certificate Issuing Authority' : 'Caste Certificate Issuing Authority (Optional)',
                      'casteCertificateAuthority',
                      CASTE_AUTHORITIES,
                      { isOptional: formData.hasCasteCertificate === 'Applied but not Received' }
                    )}
                    {formData.casteCertificateAuthority === 'Other' && renderInput(
                      formData.hasCasteCertificate === 'Yes' ? 'Please Specify Caste Issuing Authority' : 'Please Specify Caste Issuing Authority (Optional)',
                      'casteCertificateAuthorityCustom',
                      { placeholder: 'Specify authority details', isOptional: formData.hasCasteCertificate === 'Applied but not Received' }
                    )}
                  </>
                )}
                
                {renderSelect('Having Caste Validity Certificate', 'hasCasteValidityCertificate', CERT_STATUS)}
                {formData.hasCasteValidityCertificate && formData.hasCasteValidityCertificate !== 'No' && (
                  <>
                    {renderInput('Caste Validity Certificate Number - VC Number', 'casteValidityCertificateNumber', { placeholder: 'VC Number / Receipt Number' })}
                    {renderInput(
                      formData.hasCasteValidityCertificate === 'Yes' ? 'Caste Validity Certificate Issuing Date' : 'Caste Validity Certificate Application Date',
                      'casteValidityCertificateDate',
                      { type: 'date' }
                    )}
                    {renderSelect(
                      formData.hasCasteValidityCertificate === 'Yes' ? 'Caste Validity Certificate Issuing Authority' : 'Caste Validity Certificate Issuing Authority (Optional)',
                      'casteValidityCertificateAuthority',
                      VALIDITY_AUTHORITIES,
                      { isOptional: formData.hasCasteValidityCertificate === 'Applied but not Received' }
                    )}
                    {formData.casteValidityCertificateAuthority === 'Other' && renderInput(
                      formData.hasCasteValidityCertificate === 'Yes' ? 'Please Specify Caste Validity Issuing Authority' : 'Please Specify Caste Validity Issuing Authority (Optional)',
                      'casteValidityCertificateAuthorityCustom',
                      { placeholder: 'Specify authority details', isOptional: formData.hasCasteValidityCertificate === 'Applied but not Received' }
                    )}
                  </>
                )}
              </div>
            )}

            {!isEwsEligible() && !isCasteCategory() && (
              <p style={{ color: 'var(--cet-text-muted)', fontSize: '0.9rem' }}>No specific certificate details required for your selected category.</p>
            )}
          </div>
        );

      case 'sec8a':
        return (
          <div className="cetform-section">
            <div className="cetform-conditional-badge">Applicable for Category: {formData.reservationCategory}</div>
            <div className="cetform-grid" style={{ marginTop: '1rem' }}>
              {renderSelect('Having Non Creamy Layer Certificate Valid Up to 31 March 2027', 'hasNclCertificate', CERT_STATUS)}
              {formData.hasNclCertificate && formData.hasNclCertificate !== 'No' && (
                <>
                  {renderInput('Non Creamy Layer Certificate Number (MRC No / Outward No / Receipt No)', 'nclCertificateNumber', { placeholder: 'Certificate / Receipt Number' })}
                  {renderInput(
                    formData.hasNclCertificate === 'Yes' ? 'NCL Certificate Issuing Date' : 'NCL Certificate Application Date',
                    'nclCertificateDate',
                    { type: 'date' }
                  )}
                  {renderSelect(
                    formData.hasNclCertificate === 'Yes' ? 'NCL Certificate Issuing Authority' : 'NCL Certificate Issuing Authority (Optional)',
                    'nclCertificateAuthority',
                    CASTE_AUTHORITIES,
                    { isOptional: formData.hasNclCertificate === 'Applied but not Received' }
                  )}
                  {formData.nclCertificateAuthority === 'Other' && renderInput(
                    formData.hasNclCertificate === 'Yes' ? 'Please Specify NCL Issuing Authority' : 'Please Specify NCL Issuing Authority (Optional)',
                    'nclCertificateAuthorityCustom',
                    { placeholder: 'Specify authority details', isOptional: formData.hasNclCertificate === 'Applied but not Received' }
                  )}
                </>
              )}
            </div>
          </div>
        );

      case 'sec9':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderRadioGroup(
                'Person With Disability (PWD)',
                'pwd',
                YES_NO,
                { marathiLabel: 'दिव्यांग व्यक्ती (PWD) आहात का?' }
              )}
              {renderRadioGroup(
                'Linguistic Minority (Check Mother Tongue in Class 12th LC/TC)',
                'linguisticMinority',
                YES_NO,
                { marathiLabel: 'भाषाई अल्पसंख्याक (१२ वी टीसी / एलसी मधील मातृभाषा तपासा)' }
              )}
              {renderSelect('Mother Tongue', 'motherTongue', MOTHER_TONGUES)}
            </div>
          </div>
        );

      case 'sec10':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderSelect('Year of SSC / 10th Passed', 'sscPassingYear', PASSING_YEARS)}
              {renderSelect('Select any one language taken in SSC / 10th Standard', 'sscLanguageTaken', SSC_LANGUAGES)}
              {renderSelect('State of SSC / 10th Passed', 'sscState', INDIAN_STATES)}
              {renderInput('District of SSC / 10th Passed', 'sscDistrict', { placeholder: 'Enter district' })}
              {renderInput('Taluka of SSC / 10th Passed', 'sscTaluka', { placeholder: 'Taluka' })}
              {renderSelect('SSC / 10 Passing Board', 'sscBoard', BOARDS)}
            </div>
          </div>
        );

      case 'sec11':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderSelect('HSC / 12th Passing Year', 'hscPassingYear', PASSING_YEARS)}
              {renderInput('HSC / 12th Exam Seat Number / Roll Number (Alphanumeric)', 'hscSeatNumber', { placeholder: 'e.g. MH24100123 or 123456' })}
              {renderSelect('State of HSC / 12th Passed', 'hscState', INDIAN_STATES)}
              {renderInput('District of HSC / 12th Passed', 'hscDistrict', { placeholder: 'Enter district' })}
              {renderInput('Taluka of HSC / 12th Passed', 'hscTaluka', { placeholder: 'Taluka' })}
              {renderSelect('HSC / 12 Passing Board', 'hscBoard', BOARDS)}
            </div>
          </div>
        );

      case 'sec12':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              {renderInput('HSC / 12th Physics Marks', 'hscPhysicsMarks', { placeholder: 'Marks' })}
              {renderInput('HSC / 12th Chemistry Marks', 'hscChemistryMarks', { placeholder: 'Marks' })}
              {renderInput('HSC / 12th Biology Marks', 'hscBiologyMarks', { placeholder: 'Marks' })}
              {renderInput('HSC / 12th English Marks', 'hscEnglishMarks', { placeholder: 'Marks' })}
            </div>
          </div>
        );

      case 'sec13':
        return (
          <div className="cetform-section">
            <div className="cetform-grid">
              <div className="cetform-group full-width">
                <label className="cetform-label">Special / Parallel Reservation Category (Multiple Choice)<span className="cetform-req">*</span></label>
                <div className="cetform-checkbox-group">
                  {SPECIAL_RESERVATIONS.map(sr => (
                    <label key={sr} className={`cetform-checkbox-item ${(formData.specialReservation || []).includes(sr) ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={(formData.specialReservation || []).includes(sr)}
                        onChange={() => handleSpecialReservation(sr)}
                      />
                      <span>{sr}</span>
                    </label>
                  ))}
                  <label className={`cetform-checkbox-item none ${(formData.specialReservation || []).includes('None') ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={(formData.specialReservation || []).includes('None')}
                      onChange={() => handleSpecialReservation('None')}
                    />
                    <span>None</span>
                  </label>
                </div>
                {errors.specialReservation && <span className="cetform-error">{errors.specialReservation}</span>}
              </div>
            </div>

            {isHillySelected() && (
              <div className="cetform-conditional" style={{ marginTop: '1rem' }}>
                <div className="cetform-conditional-badge">Hilly Area Details</div>
                <div className="cetform-grid" style={{ marginTop: '0.75rem' }}>
                  {renderInput('Name of Hilly Village', 'hillyVillageName', { placeholder: 'Village Name' })}
                  {renderInput('If Hilly Area then Name of Hilly Taluka', 'hillyTalukaName', { placeholder: 'Taluka Name' })}
                </div>
              </div>
            )}
          </div>
        );

      case 'sec14':
        return (
          <div className="cetform-section cetform-review-page">
            <div className="cetform-review-header">
              <i className="fas fa-clipboard-check" style={{ color: 'var(--cet-accent-blue)', fontSize: '1.5rem', marginRight: '0.5rem' }}></i>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#fff' }}>Final Registration Summary Review</h3>
            </div>
            <p style={{ color: 'var(--cet-text-muted)', fontSize: '0.88rem', margin: '0.5rem 0 1.5rem 0' }}>
              Please carefully review all your entered details below line-by-line before submitting your registration. Click any <strong>Edit</strong> button to jump back and update a section.
            </p>

            <div className="cetform-review-blocks">
              {/* Block 1: Registration & Office */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-id-card"></i> 1. Registration & Office</span>
                  <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec1')}><i className="fas fa-edit"></i> Edit</button>
                </div>
                <div className="cetform-review-line"><strong>Reg./Receipt Number:</strong> <span>{formData.regReceiptNumber || '—'}</span></div>
                <div className="cetform-review-line"><strong>Registered Office:</strong> <span>{formData.registeredWith || '—'}</span></div>
              </div>

              {/* Block 2: NEET UG 2026 Details */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-flask"></i> 2. NEET UG 2026 Details</span>
                  <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec2')}><i className="fas fa-edit"></i> Edit</button>
                </div>
                <div className="cetform-review-line"><strong>NEET Roll Number:</strong> <span>{formData.neetRollNumber || '—'}</span></div>
                <div className="cetform-review-line"><strong>NEET Application Number:</strong> <span>{formData.neetApplicationNumber || '—'}</span></div>
                <div className="cetform-review-line"><strong>NEET Score (out of 720):</strong> <span>{formData.neetScore || '—'}</span></div>
                <div className="cetform-review-line"><strong>NEET All India Rank:</strong> <span>{formData.neetAIRank || '—'}</span></div>
              </div>

              {/* Block 3: Personal & Contact Details */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-user"></i> 3. Personal & Contact Details</span>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec4')}><i className="fas fa-edit"></i> Personal</button>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec4b')}><i className="fas fa-edit"></i> Contact</button>
                  </div>
                </div>
                <div className="cetform-review-line"><strong>Name Changed After 10th/Marriage:</strong> <span>{formData.nameChanged || '—'}</span></div>
                <div className="cetform-review-line"><strong>Student Full Name:</strong> <span>{getFullName() || '—'}</span></div>
                <div className="cetform-review-line"><strong>Father's Name:</strong> <span>{formData.fathersName || '—'}</span></div>
                <div className="cetform-review-line"><strong>Surname:</strong> <span>{formData.surname || '—'}</span></div>
                <div className="cetform-review-line"><strong>Mother's Name:</strong> <span>{formData.mothersName || '—'}</span></div>
                <div className="cetform-review-line"><strong>Gender:</strong> <span>{formData.gender || '—'}</span></div>
                <div className="cetform-review-line"><strong>Date of Birth:</strong> <span>{formData.dateOfBirth || '—'}</span></div>
                <div className="cetform-review-line"><strong>Mobile Number:</strong> <span>{formData.mobileNumber || '—'}</span></div>
                <div className="cetform-review-line"><strong>Email ID:</strong> <span>{formData.emailId || '—'}</span></div>
                <div className="cetform-review-line"><strong>Alternate Mobile:</strong> <span>{formData.alternateMobile || '—'}</span></div>
                <div className="cetform-review-line"><strong>Aadhaar Card Number:</strong> <span>{formData.aadharNumber || '—'}</span></div>
                <div className="cetform-review-line"><strong>APAAR ID:</strong> <span>{formData.apaarId || '—'}</span></div>
                <div className="cetform-review-line"><strong>Religion:</strong> <span>{formData.religion || '—'}</span></div>
              </div>

              {/* Block 4: Address & Domicile */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-map-marker-alt"></i> 4. Address & Domicile</span>
                  <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec5')}><i className="fas fa-edit"></i> Edit</button>
                </div>
                <div className="cetform-review-line"><strong>Full Address:</strong> <span>{formData.address || '—'}</span></div>
                <div className="cetform-review-line"><strong>City:</strong> <span>{formData.city || '—'}</span></div>
                <div className="cetform-review-line"><strong>Taluka:</strong> <span>{formData.taluka || '—'}</span></div>
                <div className="cetform-review-line"><strong>District:</strong> <span>{formData.district || '—'}</span></div>
                <div className="cetform-review-line"><strong>State:</strong> <span>{formData.state || '—'}</span></div>
                <div className="cetform-review-line"><strong>Pin Code:</strong> <span>{formData.pinCode || '—'}</span></div>
                <div className="cetform-review-line"><strong>Region of Residence:</strong> <span>{formData.regionOfResidence || '—'}</span></div>
                <div className="cetform-review-line"><strong>Domicile of Maharashtra:</strong> <span>{formData.domicileMaharashtra || '—'}</span></div>
              </div>

              {/* Block 5: Reservation & Category */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-rupee-sign"></i> 5. Reservation & Income</span>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec6')}><i className="fas fa-edit"></i> Category</button>
                    {(isEwsEligible() || isCasteCategory()) && (
                      <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec8')}><i className="fas fa-edit"></i> Certificates</button>
                    )}
                  </div>
                </div>
                <div className="cetform-review-line"><strong>Family Annual Income:</strong> <span>{formData.familyAnnualIncome || '—'}</span></div>
                <div className="cetform-review-line"><strong>Reservation Category:</strong> <span>{formData.reservationCategory || '—'}</span></div>
                {isEwsEligible() && (
                  <>
                    <div className="cetform-review-line"><strong>State EWS Certificate:</strong> <span>{formData.hasEwsCertificate || '—'}</span></div>
                    {formData.ewsCertificateNumber && (
                      <>
                        <div className="cetform-review-line"><strong>EWS Certificate No:</strong> <span>{formData.ewsCertificateNumber}</span></div>
                        <div className="cetform-review-line">
                          <strong>{formData.hasEwsCertificate === 'Yes' ? 'EWS Issuing Date:' : 'EWS Application Date:'}</strong>{' '}
                          <span>{formData.ewsCertificateDate || '—'}</span>
                        </div>
                        <div className="cetform-review-line">
                          <strong>EWS Issuing Authority:</strong>{' '}
                          <span>
                            {formData.ewsCertificateAuthority === 'Other'
                              ? formData.ewsCertificateAuthorityCustom || 'Other (unspecified)'
                              : formData.ewsCertificateAuthority || '—'}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
                {isCasteCategory() && (
                  <>
                    <div className="cetform-review-line"><strong>Caste Certificate:</strong> <span>{formData.hasCasteCertificate || '—'}</span></div>
                    {formData.casteCertificateNumber && (
                      <>
                        <div className="cetform-review-line"><strong>Caste Certificate No:</strong> <span>{formData.casteCertificateNumber}</span></div>
                        <div className="cetform-review-line">
                          <strong>{formData.hasCasteCertificate === 'Yes' ? 'Caste Issuing Date:' : 'Caste Application Date:'}</strong>{' '}
                          <span>{formData.casteCertificateDate || '—'}</span>
                        </div>
                        <div className="cetform-review-line">
                          <strong>Caste Issuing Authority:</strong>{' '}
                          <span>
                            {formData.casteCertificateAuthority === 'Other'
                              ? formData.casteCertificateAuthorityCustom || 'Other (unspecified)'
                              : formData.casteCertificateAuthority || '—'}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="cetform-review-line"><strong>Caste Validity Certificate:</strong> <span>{formData.hasCasteValidityCertificate || '—'}</span></div>
                    {formData.casteValidityCertificateNumber && (
                      <>
                        <div className="cetform-review-line"><strong>Caste Validity No:</strong> <span>{formData.casteValidityCertificateNumber}</span></div>
                        <div className="cetform-review-line">
                          <strong>{formData.hasCasteValidityCertificate === 'Yes' ? 'Validity Issuing Date:' : 'Validity Application Date:'}</strong>{' '}
                          <span>{formData.casteValidityCertificateDate || '—'}</span>
                        </div>
                        <div className="cetform-review-line">
                          <strong>Validity Issuing Authority:</strong>{' '}
                          <span>
                            {formData.casteValidityCertificateAuthority === 'Other'
                              ? formData.casteValidityCertificateAuthorityCustom || 'Other (unspecified)'
                              : formData.casteValidityCertificateAuthority || '—'}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
                {isNclCategory() && (
                  <>
                    <div className="cetform-review-line"><strong>Non-Creamy Layer Certificate:</strong> <span>{formData.hasNclCertificate || '—'}</span></div>
                    {formData.nclCertificateNumber && (
                      <>
                        <div className="cetform-review-line"><strong>NCL Certificate No:</strong> <span>{formData.nclCertificateNumber}</span></div>
                        <div className="cetform-review-line">
                          <strong>{formData.hasNclCertificate === 'Yes' ? 'NCL Issuing Date:' : 'NCL Application Date:'}</strong>{' '}
                          <span>{formData.nclCertificateDate || '—'}</span>
                        </div>
                        <div className="cetform-review-line">
                          <strong>NCL Issuing Authority:</strong>{' '}
                          <span>
                            {formData.nclCertificateAuthority === 'Other'
                              ? formData.nclCertificateAuthorityCustom || 'Other (unspecified)'
                              : formData.nclCertificateAuthority || '—'}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Block 6: PWD & Minority Status */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-wheelchair"></i> 6. Defence, PWD & Minority</span>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec3')}><i className="fas fa-edit"></i> Defence</button>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec9')}><i className="fas fa-edit"></i> PWD/Minority</button>
                  </div>
                </div>
                <div className="cetform-review-line"><strong>Parent Deputation/Defence:</strong> <span>{formData.parentDeputation || '—'}</span></div>
                <div className="cetform-review-line"><strong>Person With Disability (PWD):</strong> <span>{formData.pwd || '—'}</span></div>
                <div className="cetform-review-line"><strong>Linguistic Minority:</strong> <span>{formData.linguisticMinority || '—'}</span></div>
                <div className="cetform-review-line"><strong>Mother Tongue:</strong> <span>{formData.motherTongue || '—'}</span></div>
              </div>

              {/* Block 7: Academic Details */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-graduation-cap"></i> 7. Academic Details (SSC & HSC)</span>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec10')}><i className="fas fa-edit"></i> SSC</button>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec11')}><i className="fas fa-edit"></i> HSC</button>
                    <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec12')}><i className="fas fa-edit"></i> Marks</button>
                  </div>
                </div>
                <div className="cetform-review-line"><strong>SSC Passing Year:</strong> <span>{formData.sscPassingYear || '—'}</span></div>
                <div className="cetform-review-line"><strong>SSC Language Taken:</strong> <span>{formData.sscLanguageTaken || '—'}</span></div>
                <div className="cetform-review-line"><strong>SSC State / Board:</strong> <span>{formData.sscState || '—'} ({formData.sscBoard || '—'})</span></div>
                <div className="cetform-review-line"><strong>SSC District / Taluka:</strong> <span>{formData.sscDistrict || '—'}, {formData.sscTaluka || '—'}</span></div>
                <div className="cetform-review-line"><strong>HSC Passing Year:</strong> <span>{formData.hscPassingYear || '—'}</span></div>
                <div className="cetform-review-line"><strong>HSC Seat / Roll No:</strong> <span>{formData.hscSeatNumber || '—'}</span></div>
                <div className="cetform-review-line"><strong>HSC State / Board:</strong> <span>{formData.hscState || '—'} ({formData.hscBoard || '—'})</span></div>
                <div className="cetform-review-line"><strong>HSC District / Taluka:</strong> <span>{formData.hscDistrict || '—'}, {formData.hscTaluka || '—'}</span></div>
                <div className="cetform-review-line"><strong>HSC Marks (Physics):</strong> <span>{formData.hscPhysicsMarks || '0'} / 100</span></div>
                <div className="cetform-review-line"><strong>HSC Marks (Chemistry):</strong> <span>{formData.hscChemistryMarks || '0'} / 100</span></div>
                <div className="cetform-review-line"><strong>HSC Marks (Biology):</strong> <span>{formData.hscBiologyMarks || '0'} / 100</span></div>
                <div className="cetform-review-line"><strong>HSC Marks (English):</strong> <span>{formData.hscEnglishMarks || '0'} / 100</span></div>
              </div>

              {/* Block 8: Special Reservation */}
              <div className="cetform-review-card">
                <div className="cetform-review-card-title">
                  <span><i className="fas fa-star"></i> 8. Special Reservation</span>
                  <button type="button" className="cetform-review-edit-btn" onClick={() => goToSection('sec13')}><i className="fas fa-edit"></i> Edit</button>
                </div>
                <div className="cetform-review-line"><strong>Special Reservation:</strong> <span>{(formData.specialReservation || []).join(', ') || 'None'}</span></div>
                {isHillySelected() && (
                  <>
                    <div className="cetform-review-line"><strong>Hilly Village Name:</strong> <span>{formData.hillyVillageName || '—'}</span></div>
                    <div className="cetform-review-line"><strong>Hilly Taluka Name:</strong> <span>{formData.hillyTalukaName || '—'}</span></div>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ─── PDF DOWNLOAD ────────────────────────────────────────
  const handleDownloadPDF = () => {
    const element = pdfRef.current;
    if (!element) return;

    import('html2pdf.js').then(html2pdfModule => {
      const html2pdf = html2pdfModule.default;
      html2pdf()
        .set({
          margin: [4, 4, 4, 4],
          filename: `AME_Registration_${formData.regReceiptNumber || 'form'}_${(formData.studentName || 'student').replace(/\s+/g, '_')}.pdf`,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: 794,
            windowWidth: 794
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(element)
        .save()
        .then(() => {
          // capture done
        })
        .catch(() => {
          // error handling
        });
    });
  };

  // ─── SUCCESS SCREEN ────────────────────────────────────
  if (submitted) {
    return (
      <div className="cetform-container">
        <Helmet><title>Registration Submitted | Admissions Made Easy</title></Helmet>
        <div className="cetform-success">
          <div className="cetform-success-icon"><i className="fas fa-check-circle"></i></div>
          <h2>Registration Submitted Successfully!</h2>
          <p>Your CET-CELL registration details have been recorded. Our team will contact you shortly.</p>
          <div className="cetform-success-details">
            <span><strong>Name:</strong> {getFullName()}</span>
            <span><strong>Mobile:</strong> {formData.mobileNumber}</span>
            <span><strong>Office:</strong> {formData.registeredWith}</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button className="cetform-btn cetform-btn-pdf" onClick={handleDownloadPDF}>
              <i className="fas fa-file-pdf" style={{ marginRight: 8 }}></i>Download PDF
            </button>
            <button className="cetform-btn cetform-btn-primary" onClick={() => navigate('/')}>
              <i className="fas fa-home" style={{ marginRight: 8 }}></i>Back to Home
            </button>
          </div>
        </div>

        {/* HIDDEN TEMPLATE FOR PDF EXPORT */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <div 
            ref={pdfRef} 
            style={{ 
              background: '#ffffff', 
              color: '#111111', 
              fontFamily: 'Arial, Helvetica, sans-serif', 
              padding: '24px', 
              width: '794px', 
              margin: '0 auto',
              boxSizing: 'border-box'
            }}
          >
            {/* ═══ HEADER ═══ */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: '21px', fontWeight: '900', color: '#111', lineHeight: '1.1', letterSpacing: '-0.3px' }}>
                      Admissions <span style={{ color: '#0ea5e9' }}>Made Easy</span>
                    </div>
                    <div style={{ fontSize: '8px', color: '#999', letterSpacing: '1.4px', marginTop: '4px', textTransform: 'uppercase' }}>
                      Smart CET-CELL Registration Assistant
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', verticalAlign: 'top' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#0ea5e9', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                      CET-CELL Registration Details
                    </div>
                    <div style={{ fontSize: '9px', color: '#555', marginTop: '3px' }}>
                      {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>
                      Reg. No: <strong>{formData.regReceiptNumber || '—'}</strong> &nbsp;|&nbsp; Office: <strong>{formData.registeredWith || '—'}</strong>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* BLUE DIVIDER */}
            <div style={{ height: '2.5px', background: '#0ea5e9', marginBottom: '14px' }}></div>

            {/* ═══ STUDENT SUMMARY CARD ═══ */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', marginBottom: '12px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '33%', padding: '12px 14px', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>Student Name</div>
                    <div style={{ fontSize: '14px', fontWeight: '800', color: '#111', marginTop: '3px', lineHeight: '1.2' }}>
                      {getFullName() || '—'}
                    </div>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600', marginTop: '10px' }}>Reservation Category</div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#0ea5e9', marginTop: '3px' }}>{formData.reservationCategory || '—'}</div>
                  </td>
                  <td style={{ width: '33%', padding: '12px 14px', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>NEET Score (out of 720)</div>
                    <div style={{ fontSize: '22px', fontWeight: '900', color: '#0ea5e9', marginTop: '3px', lineHeight: '1' }}>{formData.neetScore || '—'}</div>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600', marginTop: '10px' }}>Mobile Number</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#111', marginTop: '3px' }}>{formData.mobileNumber || '—'}</div>
                  </td>
                  <td style={{ width: '34%', padding: '12px 14px', verticalAlign: 'top' }}>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>All India Rank (AIR)</div>
                    <div style={{ fontSize: '22px', fontWeight: '900', color: '#111', marginTop: '3px', lineHeight: '1' }}>
                      {formData.neetAIRank ? parseInt(formData.neetAIRank).toLocaleString('en-IN') : '—'}
                    </div>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600', marginTop: '10px' }}>NEET Roll Number</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#111', marginTop: '3px' }}>{formData.neetRollNumber || '—'}</div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ═══ DISCLAIMER BOX ═══ */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1.5px solid #fca5a5', background: '#fff5f5', marginBottom: '14px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: '9px', color: '#dc2626', fontWeight: '700', lineHeight: '1.5' }}>
                      &#9888; DISCLAIMER: This is NOT an official CET-CELL Registration Form. This document is a Demo Registration Details Form created by Admissions Made Easy for internal reference and guidance purposes only.
                    </div>
                    <div style={{ fontSize: '8.5px', color: '#b91c1c', fontWeight: '500', marginTop: '5px', lineHeight: '1.5' }}>
                      &#9888; सूचना: हा CET-CELL चा अधिकृत नोंदणी अर्ज नाही. हे दस्तऐवज केवळ Admissions Made Easy द्वारे अंतर्गत संदर्भ आणि मार्गदर्शनासाठी तयार केलेला डेमो नोंदणी तपशील फॉर्म आहे.
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ═══ 2-COLUMN ROWS ═══ */}

            {/* Row 1 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '49%', verticalAlign: 'top', paddingRight: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(1, 'Registration & Office')}
                        {dr('Reg./Receipt Number', formData.regReceiptNumber, false)}
                        {dr('Registered Office', formData.registeredWith, true)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: '51%', verticalAlign: 'top', paddingLeft: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(2, 'NEET UG 2026 Details')}
                        {dr('NEET Roll Number', formData.neetRollNumber, false)}
                        {dr('Application Number', formData.neetApplicationNumber, true)}
                        {dr('Score (out of 720)', formData.neetScore, false)}
                        {dr('All India Rank', formData.neetAIRank ? parseInt(formData.neetAIRank).toLocaleString('en-IN') : '', true)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Row 2 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '49%', verticalAlign: 'top', paddingRight: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(3, 'Personal Details')}
                        {dr('Full Name', getFullName(), false)}
                        {dr("Father's Name", formData.fathersName, true)}
                        {dr("Mother's Name", formData.mothersName, false)}
                        {dr('Gender', formData.gender, true)}
                        {dr('Date of Birth', formData.dateOfBirth, false)}
                        {dr('Religion', formData.religion, true)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: '51%', verticalAlign: 'top', paddingLeft: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(4, 'Contact & Identity')}
                        {dr('Mobile Number', formData.mobileNumber, false)}
                        {dr('Email ID', formData.emailId, true)}
                        {dr('Alternate Mobile', formData.alternateMobile, false)}
                        {dr('Aadhaar Number', formData.aadharNumber, true)}
                        {dr('APAAR ID', formData.apaarId, false)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Row 3 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '49%', verticalAlign: 'top', paddingRight: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(5, 'Address & Domicile')}
                        {dr('Full Address', formData.address, false)}
                        {dr('City', formData.city, true)}
                        {dr('Taluka / District', `${formData.taluka || '—'} / ${formData.district || '—'}`, false)}
                        {dr('State & Pin Code', `${formData.state || '—'} — ${formData.pinCode || '—'}`, true)}
                        {dr('Region of Residence', formData.regionOfResidence, false)}
                        {dr('Domicile Maharashtra', formData.domicileMaharashtra, true)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: '51%', verticalAlign: 'top', paddingLeft: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(6, 'Reservation & Income')}
                        {dr('Family Annual Income', formData.familyAnnualIncome, false)}
                        {dr('Reservation Category', formData.reservationCategory, true)}
                        {formData.hasEwsCertificate && dr('EWS Certificate', `${formData.hasEwsCertificate}${formData.ewsCertificateNumber ? ' | No: ' + formData.ewsCertificateNumber : ''}${formData.ewsCertificateDate ? ' | Date: ' + formData.ewsCertificateDate : ''}${formData.ewsCertificateAuthority ? ' | Auth: ' + (formData.ewsCertificateAuthority === 'Other' ? formData.ewsCertificateAuthorityCustom : formData.ewsCertificateAuthority) : ''}`, false)}
                        {formData.hasCasteCertificate && dr('Caste Certificate', `${formData.hasCasteCertificate}${formData.casteCertificateNumber ? ' | No: ' + formData.casteCertificateNumber : ''}${formData.casteCertificateDate ? ' | Date: ' + formData.casteCertificateDate : ''}${formData.casteCertificateAuthority ? ' | Auth: ' + (formData.casteCertificateAuthority === 'Other' ? formData.casteCertificateAuthorityCustom : formData.casteCertificateAuthority) : ''}`, true)}
                        {formData.hasCasteValidityCertificate && dr('Caste Validity', `${formData.hasCasteValidityCertificate}${formData.casteValidityCertificateNumber ? ' | No: ' + formData.casteValidityCertificateNumber : ''}${formData.casteValidityCertificateDate ? ' | Date: ' + formData.casteValidityCertificateDate : ''}${formData.hasCasteValidityCertificate === 'Yes' && formData.casteValidityCertificateAuthority ? ' | Auth: ' + (formData.casteValidityCertificateAuthority === 'Other' ? formData.casteValidityCertificateAuthorityCustom : formData.casteValidityCertificateAuthority) : ''}`, false)}
                        {formData.hasNclCertificate && dr('NCL Certificate', `${formData.hasNclCertificate}${formData.nclCertificateNumber ? ' | No: ' + formData.nclCertificateNumber : ''}${formData.nclCertificateDate ? ' | Date: ' + formData.nclCertificateDate : ''}${formData.hasNclCertificate === 'Yes' && formData.nclCertificateAuthority ? ' | Auth: ' + (formData.nclCertificateAuthority === 'Other' ? formData.nclCertificateAuthorityCustom : formData.nclCertificateAuthority) : ''}`, true)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Row 4 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '49%', verticalAlign: 'top', paddingRight: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(7, 'Defence, PWD & Minority')}
                        {dr('Parent Deputation/Defence', formData.parentDeputation, false)}
                        {dr('Person With Disability', formData.pwd, true)}
                        {dr('Linguistic Minority', formData.linguisticMinority, false)}
                        {dr('Mother Tongue', formData.motherTongue, true)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: '51%', verticalAlign: 'top', paddingLeft: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(8, 'Special Reservation')}
                        {dr('Special Reservation', (formData.specialReservation || []).join(', ') || 'None', false)}
                        {formData.hillyVillageName && dr('Hilly Village / Taluka', `${formData.hillyVillageName}, ${formData.hillyTalukaName}`, true)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* SSC Full Width */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px', marginBottom: '10px' }}>
              <tbody>
                {sh(9, 'SSC / 10th Academic Details')}
                <tr>
                  <td style={{ padding: 0, verticalAlign: 'top', width: '50%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {dr('SSC Passing Year', formData.sscPassingYear, false)}
                        {dr('Language Taken', formData.sscLanguageTaken, true)}
                        {dr('SSC Board', formData.sscBoard, false)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ padding: 0, verticalAlign: 'top', borderLeft: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {dr('State', formData.sscState, false)}
                        {dr('District', formData.sscDistrict, true)}
                        {dr('Taluka', formData.sscTaluka, false)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* HSC Full Width */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px', marginBottom: '10px' }}>
              <tbody>
                {sh(10, 'HSC / 12th Academic Details')}
                <tr>
                  <td style={{ padding: 0, verticalAlign: 'top', width: '50%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {dr('HSC Passing Year', formData.hscPassingYear, false)}
                        {dr('Seat / Roll No', formData.hscSeatNumber, true)}
                        {dr('HSC Board', formData.hscBoard, false)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ padding: 0, verticalAlign: 'top', borderLeft: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {dr('State', formData.hscState, false)}
                        {dr('District', formData.hscDistrict, true)}
                        {dr('Taluka', formData.hscTaluka, false)}
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr style={{ background: '#f9fafb' }}>
                  <td colSpan={2} style={{ padding: '7px 12px', fontSize: '10px', color: '#444', borderTop: '1px solid #e2e8f0' }}>
                    <strong>HSC Marks:</strong> &nbsp;
                    Physics: <strong>{formData.hscPhysicsMarks || '0'}/100</strong> &nbsp;|&nbsp;
                    Chemistry: <strong>{formData.hscChemistryMarks || '0'}/100</strong> &nbsp;|&nbsp;
                    Biology: <strong>{formData.hscBiologyMarks || '0'}/100</strong> &nbsp;|&nbsp;
                    English: <strong>{formData.hscEnglishMarks || '0'}/100</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* FOOTER */}
            <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: '1.5px solid #e2e8f0', marginTop: '6px' }}>
              <tbody>
                <tr>
                  <td style={{ paddingTop: '8px', fontSize: '8px', color: '#aaa', verticalAlign: 'middle' }}>
                    Auto-generated by <strong style={{ color: '#0ea5e9' }}>Admissions Made Easy</strong> &nbsp;|&nbsp; For internal reference only
                  </td>
                  <td style={{ paddingTop: '8px', fontSize: '8px', color: '#aaa', textAlign: 'right', verticalAlign: 'middle' }}>
                    {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ─── MAIN RENDER ───────────────────────────────────────
  const isFinalStep = stepIndex === activeSteps.length - 1;
  const progressPercent = ((stepIndex + 1) / activeSteps.length) * 100;

  if (selectedForm === null) {
    return (
      <div className="cetform-wrapper">
        <Helmet>
          <title>Medical Registration Details Forms | Admissions Made Easy</title>
          <meta name="description" content="Select a medical admissions registration details form to begin your application process." />
        </Helmet>
        
        <div className="cetform-container">
          <div className="cetform-header">
            <div className="cetform-header-logo">
              <i className="fas fa-file-invoice"></i>
            </div>
            <h1>Medical Registration Details Forms</h1>
            <p>Select a registration detail form to begin your process.</p>
            <p style={{ color: '#ef4444', fontWeight: '600', marginTop: '0.5rem', fontSize: '0.85rem', letterSpacing: '0.3px' }}>
              * This is only for the Admissions Made Easy offices reference only
            </p>
          </div>
          
          <div className="cetform-hub-grid">
            <div className={`cetform-hub-card ${settings.form_state_active ? 'active' : 'disabled'}`}>
              <div className="cetform-hub-icon">
                <i className="fas fa-graduation-cap"></i>
              </div>
              {!settings.form_state_active && <div className="cetform-hub-badge" style={{ background: '#64748b' }}>Offline</div>}
              <h3>State Quota Details</h3>
              <p>Fill your NEET UG 2026 academic, address, category, and parallel reservation details for Maharashtra State Quota admissions.</p>
              <button 
                className={`cetform-btn ${settings.form_state_active ? 'cetform-btn-primary' : 'cetform-btn-secondary'}`} 
                onClick={() => setSelectedForm('state-quota')}
                disabled={!settings.form_state_active}
              >
                {settings.form_state_active ? 'Start (State quota Details)' : 'Offline'}
              </button>
            </div>
            
            <div className={`cetform-hub-card ${settings.form_mcc_active ? 'active' : 'disabled'}`}>
              <div className="cetform-hub-icon">
                <i className="fas fa-building"></i>
              </div>
              {!settings.form_mcc_active && <div className="cetform-hub-badge" style={{ background: '#64748b' }}>Offline</div>}
              <h3>MBBS/BDS MCC (AIIMS/JIPMER/15% AIQ/Deemed) Details</h3>
              <p>Fill your NEET UG 2026 academic, qualification, address, and refund bank details for MBBS/BDS MCC (AIIMS/JIPMER/15% AIQ/Deemed) admissions.</p>
              <button 
                className={`cetform-btn ${settings.form_mcc_active ? 'cetform-btn-primary' : 'cetform-btn-secondary'}`} 
                onClick={() => setSelectedForm('mcc-quota')}
                disabled={!settings.form_mcc_active}
              >
                {settings.form_mcc_active ? 'Start (MBBS/BDS MCC Details)' : 'Offline'}
              </button>
            </div>

            <div className="cetform-hub-card disabled">
              <div className="cetform-hub-icon">
                <i className="fas fa-map-marked-alt"></i>
              </div>
              <div className="cetform-hub-badge">Coming Soon</div>
              <h3>Other state registration details form</h3>
              <p>Fill your NEET UG 2026 academic, address, category, and state selection details for Other State Quota admissions.</p>
              <button className="cetform-btn cetform-btn-secondary" disabled>
                Locked
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedForm === 'mcc-quota') {
    return <MccForm onBack={() => setSelectedForm(null)} />;
  }

  return (
    <div className="cetform-wrapper">
      <Helmet>
        <title>State Quota Details Form | Admissions Made Easy</title>
        <meta name="description" content="CET-CELL Registration Details Form for NEET UG 2026 Medical Admissions in Maharashtra" />
      </Helmet>

      <div className="cetform-container">
        {/* Back Button to Hub */}
        <button className="cetform-btn-back" onClick={() => setSelectedForm(null)}>
          <i className="fas fa-chevron-left"></i> Back to Dashboard
        </button>

        {/* Progress Bar & Sleek Single Section Header */}
        <div className="cetform-progress-wrapper" style={{ marginTop: '0' }}>
          <div className="cetform-progress-bar-container">
            <div className="cetform-progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <div className="cetform-active-step-header">
            <span className="cetform-active-step-badge">Page {stepIndex + 1} of {activeSteps.length}</span>
            <span className="cetform-active-step-title">{currentStep.title}</span>
          </div>
        </div>

        {/* Current Section Content */}
        <div className="cetform-step-content">
          {renderSectionContent()}
        </div>

        {/* Navigation */}
        <div className="cetform-nav">
          {stepIndex > 0 && (
            <button className="cetform-btn cetform-btn-secondary" onClick={handlePrev}>
              <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>Previous
            </button>
          )}
          <div className="cetform-nav-spacer"></div>
          {!isFinalStep ? (
            <button className="cetform-btn cetform-btn-primary" onClick={handleNext}>
              Next Section<i className="fas fa-arrow-right" style={{ marginLeft: 8 }}></i>
            </button>
          ) : (
            <button className="cetform-btn cetform-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Submitting...</>
              ) : (
                <><i className="fas fa-paper-plane" style={{ marginRight: 8 }}></i>Submit Registration</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
